import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { FloppyDisk } from "@phosphor-icons/react";
import AdminNav from "../../components/AdminNav";

const NUM_FIELDS = [
  { key: "max_piece_weight_kg", label: "Max actual weight / box (kg)", step: "0.1" },
  { key: "max_longest_side_cm", label: "Max longest side (cm)", step: "1" },
  { key: "max_second_longest_side_cm", label: "Max second-longest side (cm)", step: "1" },
  { key: "max_shortest_side_cm", label: "Max shortest side (cm)", step: "1" },
  { key: "max_lwh_sum_cm", label: "Max total dimensions (L + W + H)", step: "1" },
  { key: "max_volumetric_weight_per_box_kg", label: "Max volumetric wt / box (kg)", step: "0.1" },
];

const PACKAGE_TYPES = ["CARTON_BOX", "BAG_SACK", "CRATE", "BUNDLE", "ROLL_TUBE", "OTHER"];

export default function CargoAcceptanceSettings() {
  const { profile } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/cargo-acceptance/settings");
        setSettings(data);
      } catch (e) {
        toast.error(e.displayMessage || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (profile && profile.role !== "SUPER_ADMIN") return <Navigate to="/app" replace />;

  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const toggleType = (t) => {
    setSettings((s) => {
      const cur = Array.isArray(s.allowed_package_types) ? s.allowed_package_types : [];
      return { ...s, allowed_package_types: cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_message: settings.customer_message || null,
        allowed_package_types:
          Array.isArray(settings.allowed_package_types) && settings.allowed_package_types.length
            ? settings.allowed_package_types
            : null,
        ...Object.fromEntries(
          NUM_FIELDS.map((f) => [f.key, settings[f.key] === "" || settings[f.key] == null ? null : Number(settings[f.key])])
        ),
      };
      const { data } = await api.put("/admin/cargo-acceptance/settings", payload);
      setSettings(data);
      toast.success("Cargo Acceptance settings saved");
    } catch (e) {
      toast.error(e.displayMessage || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <div className="max-w-[1600px] mx-auto px-6 pt-6"><AdminNav /></div>
    <div className="max-w-[760px] mx-auto p-6" data-testid="cargo-acceptance-settings-page">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="overline">CONFIGURATION</div>
          <h1 className="heading text-3xl">Cargo Acceptance Profile</h1>
        </div>
      </div>

      <div className="grid-card p-4 mb-6 border-l-2 border-[hsl(var(--accent))]">
        <p className="text-sm text-[hsl(var(--muted))]">
          Global cargo acceptance envelope. Always active. <strong>Leave a numeric limit blank</strong> to
          impose no restriction from that criterion. Any configured limit that is exceeded, or a
          package type that is not allowed, is a <strong>hard block</strong> — no quote is produced.
          A completely blank profile blocks nothing.
        </p>
      </div>

      {loading || !settings ? (
        <div className="mono text-sm">Loading…</div>
      ) : (
        <div className="grid-card p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {NUM_FIELDS.map((f) => (
              <label key={f.key} className="block">
                <div className="overline mb-1.5">{f.label}</div>
                <input
                  type="number" min="0" step={f.step} placeholder="Unconfirmed"
                  className="input-field"
                  value={settings[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  data-testid={`cargo-acceptance-${f.key}`}
                />
              </label>
            ))}
          </div>

          <div>
            <div className="overline mb-1.5">Allowed package types (none selected = all allowed)</div>
            <div className="flex flex-wrap gap-2" data-testid="cargo-acceptance-allowed-types">
              {PACKAGE_TYPES.map((t) => {
                const on = Array.isArray(settings.allowed_package_types) && settings.allowed_package_types.includes(t);
                return (
                  <button
                    key={t} type="button" onClick={() => toggleType(t)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      on ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]" : "border-[hsl(var(--border))]"
                    }`}
                    data-testid={`cargo-acceptance-type-${t}`}
                  >
                    {t.replace("_", " / ")}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">
            <div className="overline mb-1.5">Customer message</div>
            <textarea
              className="input-field min-h-[70px]" rows={2}
              value={settings.customer_message ?? ""}
              onChange={(e) => setField("customer_message", e.target.value)}
              data-testid="cargo-acceptance-customer_message"
            />
          </label>

          <div className="flex justify-end pt-2">
            <button className="btn-primary inline-flex items-center gap-2" disabled={saving} onClick={save} data-testid="cargo-acceptance-save-btn">
              <FloppyDisk size={16} /> {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
